import React from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarDays, Copy, Save, Trash2, Users, Image as ImageIcon, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, ASSET_URL } from "../../lib/api";
import MediaPickerModal from "../../components/admin/MediaPickerModal";

const empty = {
  slug: "", title_de: "", title_en: "", excerpt_de: "", excerpt_en: "",
  body_de: "", body_en: "", speaker: "", speaker_bio_de: "", speaker_bio_en: "",
  starts_at: "", ends_at: "", venue_name: "", venue_address: "",
  fortbildungspunkte: 0, price_cents: 0, currency: "EUR",
  price_note: "inkl. gesetzlicher Umsatzsteuer", capacity: 0, registration_deadline: "",
  cover_media_id: null, features_de: [], features_en: [],
  bank_account_holder: "", bank_iban: "", bank_bic: "", payment_due_days: 7,
  cancellation_terms_de: "", cancellation_terms_en: "",
  published: false, status: "draft",
  seo_de: {}, seo_en: {},
};

const toLocal = value => value ? value.slice(0, 16) : "";
const fromLocal = value => value || "";
const toSlug = value => (value || "")
  .trim()
  .toLowerCase()
  .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "");

export default function EventsAdmin() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = React.useState([]);
  const [draft, setDraft] = React.useState({ ...empty });
  const [editId, setEditId] = React.useState(null);
  const [registrations, setRegistrations] = React.useState([]);
  const [media, setMedia] = React.useState([]);
  const [picker, setPicker] = React.useState(false);
  const [view, setView] = React.useState("event");
  const [slugManual, setSlugManual] = React.useState(false);

  const load = React.useCallback(() => Promise.all([
    api.adminEvents(),
    api.adminMedia().catch(() => []),
  ]).then(([events, assets]) => { setItems(events || []); setMedia(assets || []); }), []);

  React.useEffect(() => { load(); }, [load]);

  const edit = async item => {
    setEditId(item.id);
    setDraft({
      ...empty, ...item,
      starts_at: toLocal(item.starts_at),
      ends_at: toLocal(item.ends_at),
      registration_deadline: toLocal(item.registration_deadline),
    });
    setView("event");
    setSlugManual(true);
    setRegistrations(await api.eventRegistrations(item.id));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  React.useEffect(() => {
    const requestedId = searchParams.get("edit");
    if (!requestedId || !items.length || editId === requestedId) return;
    const requested = items.find(item => item.id === requestedId);
    if (!requested) return;
    edit(requested);
    setSearchParams({}, { replace: true });
    // Open a freshly generated draft only once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, searchParams, editId, setSearchParams]);

  const reset = () => {
    setDraft({ ...empty });
    setEditId(null);
    setRegistrations([]);
    setView("event");
    setSlugManual(false);
  };

  const save = async () => {
    if (!draft.slug || !draft.title_de) {
      toast.error("Titel und URL sind Pflichtfelder");
      return;
    }
    const payload = {
      ...draft,
      slug: draft.slug.trim().toLowerCase().replace(/[^a-z0-9äöüß-]+/g, "-").replace(/^-|-$/g, ""),
      starts_at: fromLocal(draft.starts_at),
      ends_at: fromLocal(draft.ends_at),
      registration_deadline: fromLocal(draft.registration_deadline),
      bank_iban: (draft.bank_iban || "").replace(/\s/g, "").toUpperCase(),
    };
    try {
      const saved = editId ? await api.updateEvent(editId, payload) : await api.createEvent(payload);
      toast.success("Veranstaltung gespeichert");
      await load();
      await edit(saved);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
    }
  };

  const duplicate = async id => {
    try {
      const copy = await api.duplicateEvent(id);
      toast.success("Entwurf wurde kopiert");
      await load();
      await edit(copy);
    } catch { toast.error("Kopieren fehlgeschlagen"); }
  };

  const remove = async id => {
    if (!window.confirm("Diese Veranstaltung wirklich löschen?")) return;
    try {
      await api.deleteEvent(id);
      toast.success("Veranstaltung gelöscht");
      reset();
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Löschen nicht möglich"); }
  };

  const updateRegistration = async (registration, patch) => {
    try {
      await api.updateEventRegistration(registration.id, patch);
      setRegistrations(await api.eventRegistrations(editId));
      toast.success(patch.payment_status === "paid" ? "Als bezahlt markiert; Bestätigung wird versendet" : "Status gespeichert");
      load();
    } catch { toast.error("Status konnte nicht gespeichert werden"); }
  };

  const asset = media.find(m => m.id === draft.cover_media_id);
  const cover = asset?.variants?.thumb || asset?.variants?.webp || asset?.variants?.original;
  const publicationMissing = [
    ["starts_at", "Beginn"],
    ["ends_at", "Ende"],
    ["venue_name", "Veranstaltungsort"],
  ].filter(([key]) => !String(draft[key] || "").trim()).map(([, label]) => label);
  if (["open", "sold_out"].includes(draft.status)) {
    if (!String(draft.cancellation_terms_de || "").trim()) publicationMissing.push("Teilnahme- und Stornierungsbedingungen");
    if ((draft.price_cents || 0) > 0 && !String(draft.bank_account_holder || "").trim()) publicationMissing.push("Kontoinhaber");
    if ((draft.price_cents || 0) > 0 && !String(draft.bank_iban || "").trim()) publicationMissing.push("IBAN");
  }
  if (draft.status === "draft") publicationMissing.push("Status (nicht Entwurf)");

  return (
    <div data-testid="admin-events">
      <div className="flex flex-col items-start gap-4 mb-7">
        <div><div className="brut-tag mb-3">CMS / AKADEMIE</div><h1 className="brut-h2">Veranstaltungen</h1></div>
        <div className="flex flex-wrap justify-start gap-2">
          <button onClick={reset} className="brut-btn brut-btn-ghost !py-2 !px-4">Neue Veranstaltung</button>
          <button onClick={save} className="brut-btn !py-2 !px-4" disabled={!draft.title_de.trim()} title={!draft.title_de.trim() ? "Bitte zuerst den Veranstaltungstitel eintragen" : "Veranstaltung als Entwurf speichern"}>
            <Save size={15}/> {editId ? "Änderungen speichern" : "Entwurf anlegen"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 xl:col-span-4 space-y-3">
          {items.map(item => (
            <div key={item.id} className={`border-2 p-4 ${editId === item.id ? "border-[var(--accent)]" : "border-[var(--ink)]"}`}>
              <div className="font-mono text-[10px] uppercase tracking-widest opacity-60">
                {item.starts_at?.slice(0, 10) || "Kein Datum"} · {item.status} · {item.published ? "online" : "Entwurf"}
              </div>
              <h2 className="brut-h3 mt-2">{item.title_de}</h2>
              <div className="flex flex-wrap gap-3 mt-3 text-xs font-mono">
                <button onClick={() => edit(item)} className="underline">Bearbeiten</button>
                <button onClick={() => duplicate(item.id)} className="underline inline-flex gap-1"><Copy size={12}/> Kopieren</button>
                <button onClick={() => remove(item.id)} className="underline text-red-600"><Trash2 size={12}/></button>
              </div>
              <div className="mt-3 text-xs"><strong>{item.booked_count || 0}</strong> Anmeldungen{item.capacity ? ` / ${item.capacity} Plätze` : ""}</div>
            </div>
          ))}
          {!items.length && <p className="opacity-60">Noch keine Veranstaltungen.</p>}
        </div>

        <div className="col-span-12 xl:col-span-8">
          {editId && (
            <div className="flex gap-2 mb-4">
              <button onClick={() => setView("event")} className={`brut-btn !py-2 !px-4 ${view !== "event" ? "brut-btn-ghost" : ""}`}><CalendarDays size={14}/> Veranstaltung</button>
              <button onClick={() => setView("registrations")} className={`brut-btn !py-2 !px-4 ${view !== "registrations" ? "brut-btn-ghost" : ""}`}><Users size={14}/> Anmeldungen ({registrations.length})</button>
              {draft.published && <a href={`/akademie/${draft.slug}`} target="_blank" rel="noreferrer" className="brut-btn brut-btn-ghost !py-2 !px-4"><ExternalLink size={14}/> Öffnen</a>}
            </div>
          )}

          {view === "event" ? (
            <div className="border-2 border-[var(--ink)] rounded-[20px] p-5 md:p-7 space-y-6">
              <Section title="Grunddaten">
                <label><span className="brut-label">Veranstaltungstitel (DE) *</span><input className="brut-input" placeholder="z. B. Endo Update 2026" value={draft.title_de} onChange={e => { const title = e.target.value; setDraft(current => ({...current, title_de:title, slug:slugManual ? current.slug : toSlug(title)})); }}/></label>
                <label><span className="brut-label">URL * <small className="normal-case tracking-normal opacity-60">(wird automatisch erzeugt)</small></span><input className="brut-input" placeholder="z. B. endo-update-2026" value={draft.slug} onChange={e => { setSlugManual(true); setDraft({...draft, slug:e.target.value}); }}/></label>
                {!editId && <p className="text-sm p-3 bg-[var(--accent)]/15 border border-[var(--accent)]"><strong>So geht's:</strong> Titel eingeben und oben auf „Entwurf anlegen“ klicken. Alle weiteren Angaben können danach in Ruhe ergänzt werden.</p>}
                <textarea className="brut-textarea" rows={3} placeholder="Kurzbeschreibung (DE)" value={draft.excerpt_de} onChange={e => setDraft({...draft, excerpt_de:e.target.value})}/>
                <textarea className="brut-textarea" rows={8} placeholder="Ausführlicher Veranstaltungstext (DE)" value={draft.body_de} onChange={e => setDraft({...draft, body_de:e.target.value})}/>
                <textarea className="brut-textarea" rows={4} placeholder={"Leistungen, eine Zeile je Eintrag\nBegrüßungskaffee\nFlying Buffet"} value={(draft.features_de || []).join("\n")} onChange={e => setDraft({...draft, features_de:e.target.value.split("\n").filter(Boolean)})}/>
              </Section>

              <Section title="Titelbild">
                {cover && <img src={ASSET_URL(cover)} alt="" className="w-full max-h-72 object-cover rounded-[16px]"/>}
                <button onClick={() => setPicker(true)} className="brut-btn brut-btn-ghost !py-2 !px-4"><ImageIcon size={14}/> Bild aus Mediathek wählen</button>
              </Section>

              <Section title="Termin und Ort" grid>
                <label><span className="brut-label">Von – Datum und Uhrzeit</span><input type="datetime-local" className="brut-input" value={draft.starts_at} onChange={e => setDraft({...draft, starts_at:e.target.value})}/></label>
                <label><span className="brut-label">Bis – Datum und Enduhrzeit</span><input type="datetime-local" className="brut-input" value={draft.ends_at} onChange={e => setDraft({...draft, ends_at:e.target.value})}/></label>
                <input className="brut-input" placeholder="Veranstaltungsort" value={draft.venue_name} onChange={e => setDraft({...draft, venue_name:e.target.value})}/>
                <input className="brut-input" placeholder="Adresse" value={draft.venue_address} onChange={e => setDraft({...draft, venue_address:e.target.value})}/>
                <input type="number" className="brut-input" placeholder="Fortbildungspunkte" value={draft.fortbildungspunkte} onChange={e => setDraft({...draft, fortbildungspunkte:Number(e.target.value)})}/>
                <label><span className="brut-label">Referent / Referentin (leer lassen, wenn nicht anzeigen)</span><input className="brut-input" placeholder="Name des Referenten" value={draft.speaker} onChange={e => setDraft({...draft, speaker:e.target.value})}/></label>
              </Section>

              <Section title="Preis, Plätze und Anmeldung" grid>
                <div className="col-span-full text-sm p-3 bg-[var(--accent)]/15 border border-[var(--accent)]">
                  <strong>Für später vorbereitet:</strong> Preis, Plätze, Anmeldeschluss und Bedingungen ergänzen. Erst danach unter „Veröffentlichung“ den Status auf „Anmeldung offen“ stellen.
                </div>
                <label><span className="brut-label">Preis in Euro</span><input type="number" step="0.01" className="brut-input" value={(draft.price_cents || 0)/100} onChange={e => setDraft({...draft, price_cents:Math.round(Number(e.target.value)*100)})}/></label>
                <label><span className="brut-label">Maximale Plätze (0 = unbegrenzt)</span><input type="number" className="brut-input" value={draft.capacity} onChange={e => setDraft({...draft, capacity:Number(e.target.value)})}/></label>
                <input className="brut-input" placeholder="Preishinweis" value={draft.price_note} onChange={e => setDraft({...draft, price_note:e.target.value})}/>
                <label><span className="brut-label">Anmeldeschluss</span><input type="datetime-local" className="brut-input" value={draft.registration_deadline} onChange={e => setDraft({...draft, registration_deadline:e.target.value})}/></label>
              </Section>

              <Section title="Überweisung" grid>
                <input className="brut-input" placeholder="Kontoinhaber" value={draft.bank_account_holder} onChange={e => setDraft({...draft, bank_account_holder:e.target.value})}/>
                <input className="brut-input" placeholder="IBAN" value={draft.bank_iban} onChange={e => setDraft({...draft, bank_iban:e.target.value})}/>
                <input className="brut-input" placeholder="BIC" value={draft.bank_bic} onChange={e => setDraft({...draft, bank_bic:e.target.value})}/>
                <label><span className="brut-label">Zahlungsziel in Tagen</span><input type="number" className="brut-input" value={draft.payment_due_days} onChange={e => setDraft({...draft, payment_due_days:Number(e.target.value)})}/></label>
              </Section>

              <Section title="Bedingungen">
                <textarea className="brut-textarea" rows={4} placeholder="Teilnahme-, Zahlungs- und Stornierungsbedingungen *" value={draft.cancellation_terms_de} onChange={e => setDraft({...draft, cancellation_terms_de:e.target.value})}/>
              </Section>

              <details className="border border-[var(--line-soft)] p-4">
                <summary className="font-mono text-xs uppercase tracking-[0.2em] cursor-pointer">Englische Inhalte</summary>
                <div className="space-y-3 mt-4">
                  <input className="brut-input" placeholder="Title (EN)" value={draft.title_en || ""} onChange={e => setDraft({...draft, title_en:e.target.value})}/>
                  <textarea className="brut-textarea" rows={3} placeholder="Short description (EN)" value={draft.excerpt_en || ""} onChange={e => setDraft({...draft, excerpt_en:e.target.value})}/>
                  <textarea className="brut-textarea" rows={7} placeholder="Event text (EN)" value={draft.body_en || ""} onChange={e => setDraft({...draft, body_en:e.target.value})}/>
                  <textarea className="brut-textarea" rows={3} placeholder="Cancellation terms (EN)" value={draft.cancellation_terms_en || ""} onChange={e => setDraft({...draft, cancellation_terms_en:e.target.value})}/>
                  <textarea className="brut-textarea" rows={4} placeholder="Included services, one per line" value={(draft.features_en || []).join("\n")} onChange={e => setDraft({...draft, features_en:e.target.value.split("\n").filter(Boolean)})}/>
                </div>
              </details>

              <details className="border border-[var(--line-soft)] p-4">
                <summary className="font-mono text-xs uppercase tracking-[0.2em] cursor-pointer">SEO / GEO</summary>
                <div className="space-y-3 mt-4">
                  <input className="brut-input" placeholder="SEO-Titel (DE)" value={draft.seo_de?.title || ""} onChange={e => setDraft({...draft, seo_de:{...(draft.seo_de||{}),title:e.target.value}})}/>
                  <textarea className="brut-textarea" rows={2} placeholder="Meta-Beschreibung (DE)" value={draft.seo_de?.description || ""} onChange={e => setDraft({...draft, seo_de:{...(draft.seo_de||{}),description:e.target.value}})}/>
                  <input className="brut-input" placeholder="SEO title (EN)" value={draft.seo_en?.title || ""} onChange={e => setDraft({...draft, seo_en:{...(draft.seo_en||{}),title:e.target.value}})}/>
                  <textarea className="brut-textarea" rows={2} placeholder="Meta description (EN)" value={draft.seo_en?.description || ""} onChange={e => setDraft({...draft, seo_en:{...(draft.seo_en||{}),description:e.target.value}})}/>
                </div>
              </details>

              <Section title="Veröffentlichung" grid>
                <label><span className="brut-label">Status</span><select className="brut-input" value={draft.status} onChange={e => setDraft({...draft, status:e.target.value})}><option value="draft">Entwurf</option><option value="open">Anmeldung offen</option><option value="sold_out">Ausgebucht</option><option value="closed">Geschlossen</option><option value="cancelled">Abgesagt</option></select></label>
                <label className="flex gap-3 items-center"><input type="checkbox" checked={draft.published} onChange={e => setDraft({...draft, published:e.target.checked})}/><strong>Öffentlich sichtbar</strong></label>
              </Section>

              {draft.published && publicationMissing.length > 0 && (
                <div className="border-2 border-red-500 bg-red-50 text-red-900 p-4 rounded-[12px]">
                  <strong>Vor der Veröffentlichung fehlt noch:</strong>
                  <ul className="list-disc pl-5 mt-2">{publicationMissing.map(item => <li key={item}>{item}</li>)}</ul>
                </div>
              )}

              <button onClick={save} className="brut-btn"><Save size={15}/> {editId ? "Änderungen speichern" : "Entwurf anlegen"}</button>
            </div>
          ) : (
            <Registrations items={registrations} onUpdate={updateRegistration}/>
          )}
        </div>
      </div>

      {picker && <MediaPickerModal filter="image" onClose={() => setPicker(false)} onSelect={m => { setDraft({...draft, cover_media_id:m.id}); setMedia(old => old.some(x=>x.id===m.id) ? old : [...old,m]); setPicker(false); }}/>}
    </div>
  );
}

function Section({ title, children, grid = false }) {
  return <section><h3 className="font-mono text-xs uppercase tracking-[0.2em] mb-3 pb-2 border-b border-[var(--line-soft)]">{title}</h3><div className={grid ? "grid md:grid-cols-2 gap-3" : "space-y-3"}>{children}</div></section>;
}

function Registrations({ items, onUpdate }) {
  if (!items.length) return <div className="border-2 border-[var(--ink)] p-8"><p>Noch keine Anmeldungen.</p></div>;
  const exportCsv = () => {
    const rows = [
      ["Referenz","Name","E-Mail","Unternehmen","Telefon","Rechnungsanschrift","Bescheinigung","Status","Zahlung","Anmeldedatum"],
      ...items.map(r => [r.reference,r.name,r.email,r.company,r.phone,r.billing_address,r.certificate_name,r.status,r.payment_status,r.created_at]),
    ];
    const csv = rows.map(row => row.map(value => `"${String(value || "").replace(/"/g,'""')}"`).join(";")).join("\r\n");
    const url = URL.createObjectURL(new Blob(["\ufeff" + csv], {type:"text/csv;charset=utf-8"}));
    const link = document.createElement("a");
    link.href = url; link.download = "veranstaltungs-anmeldungen.csv"; link.click();
    URL.revokeObjectURL(url);
  };
  return <div className="space-y-3"><button onClick={exportCsv} className="brut-btn brut-btn-ghost !py-2 !px-4">CSV exportieren</button>{items.map(r => (
    <div key={r.id} className="border-2 border-[var(--ink)] p-4">
      <div className="flex flex-wrap justify-between gap-3">
        <div><div className="font-mono text-[10px] uppercase opacity-60">{r.reference} · {r.created_at?.slice(0,10)}</div><h3 className="brut-h3">{r.name}</h3><p className="text-sm">{r.company}<br/><a className="underline" href={`mailto:${r.email}`}>{r.email}</a> · {r.phone}</p></div>
        <div className="flex flex-wrap gap-2 items-start">
          <select className="brut-input !w-auto" value={r.status} onChange={e => onUpdate(r,{status:e.target.value})}><option value="registered">Angemeldet</option><option value="waitlist">Warteliste</option><option value="cancelled">Storniert</option><option value="attended">Teilgenommen</option></select>
          <select className="brut-input !w-auto" value={r.payment_status} onChange={e => onUpdate(r,{payment_status:e.target.value})}><option value="open">Zahlung offen</option><option value="paid">Bezahlt</option><option value="refunded">Erstattet</option><option value="not_required">Nicht erforderlich</option></select>
        </div>
      </div>
      <details className="mt-3 text-sm"><summary className="cursor-pointer font-bold">Weitere Angaben</summary><div className="mt-2 whitespace-pre-line">Rechnungsanschrift: {r.billing_address}<br/>Bescheinigung: {r.certificate_name}<br/>Ernährung: {r.dietary_notes || "—"}</div></details>
    </div>
  ))}</div>;
}
